import { apiSlice } from '../../../store/api/apiSlice';

export interface EvalSuite {
  id: string;
  name: string;
  description: string;
  prompt_id?: string;
  tags: string[];
  default_providers: string[];
  default_profile_slug?: string;
  ci_enabled: boolean;
  created_at: string;
}

export type CaseSourceType = 'manual' | 'production_trace';

export interface EvalCase {
  id: string;
  suite_id: string;
  name: string;
  description: string;
  prompt_ref: { prompt_id: string; version: number };
  input_config: InputConfig;
  assertions: Assertion[];
  target_config: TargetConfig;
  scoring_config: ScoringConfig;
  fixture_set_id?: string;
  source_type: CaseSourceType;
  source_trace_id: string;
  source_reviewed: boolean;
  enabled: boolean;
  stale: boolean;
  stale_since_version?: number;
}

export interface ScenarioContext {
  person_id?: string;
  org_id?: string;
  transcript?: string;
  existing_artifact?: string;
  document_ids?: string[];
  draft_bundle_id?: string;
}

export interface InputConfig {
  type: 'text_generation' | 'artifact_generation' | 'artifact_chat' | 'agent_behavior';
  variables: Record<string, unknown>;
  messages?: { role: string; content: string }[];
  context?: ScenarioContext;
}

export interface Assertion {
  type:
    | 'contains'
    | 'not_contains'
    | 'is_json'
    | 'json_path'
    | 'regex'
    | 'min_length'
    | 'max_length'
    | 'judge'
    | 'tool_call_sequence';
  value?: unknown;
  path?: string;
  operator?: string;
  rubric?: string;
  threshold?: number;
  weight?: number;
}

export interface TargetConfig {
  profile_slug?: string;
  providers: string[];
}

export interface ScoringConfig {
  pass_threshold?: number;
  dimensions?: string[];
}

export interface EvalFixtureSet {
  id: string;
  name: string;
  version: number;
  description: string;
  identity_context?: Record<string, unknown>;
  document_context?: Record<string, unknown>;
  template_variables: Record<string, unknown>;
  authoring_config?: FixtureAuthoringConfig | null;
  archived_at?: string | null;
  created_at?: string;
  updated_at: string;
}

export interface FixtureIdentityDefaults {
  person_id?: string;
  org_id?: string;
  draft_bundle_id?: string;
}

export interface FixtureAnonymizationAttestation {
  status: string;
  notes?: string;
}

export type FixtureShape = 'single_transcript' | 'transcript_bundle';

export interface FixtureExampleContext {
  display_label?: string;
  notes?: string;
}

export interface FixtureTranscriptItem {
  id: string;
  title?: string;
  session_date?: string;
  mime_type?: string;
  content?: string;
  markdown?: string;
}

export interface FixtureAuthoringConfig {
  kind: 'anonymized_transcript_bundle';
  fixture_shape?: FixtureShape;
  example_context?: FixtureExampleContext;
  identity_defaults?: FixtureIdentityDefaults;
  template_variables?: Record<string, unknown>;
  primary_transcript_id?: string;
  expose_primary_as_transcript?: boolean;
  anonymization: FixtureAnonymizationAttestation;
  transcripts: FixtureTranscriptItem[];
}

export interface EvalFixturePreviewSummary {
  transcript_count: number;
  document_ids: string[];
  primary_transcript_id?: string;
  injects_transcript: boolean;
  identity_keys: string[];
  template_variable_keys: string[];
}

export interface FixtureSetCaseReference {
  case_id: string;
  case_name: string;
  suite_id: string;
  suite_name: string;
  enabled: boolean;
}

export interface EvalCompiledRuntime {
  identity_context: Record<string, unknown>;
  document_context: Record<string, unknown>;
  template_variables: Record<string, unknown>;
  summary: EvalFixturePreviewSummary;
}

export interface EvalFixtureDetailResponse {
  fixture: EvalFixtureSet;
  preview_summary: EvalFixturePreviewSummary;
  case_references: FixtureSetCaseReference[];
}

export interface EvalRun {
  id: string;
  case_id: string;
  triggered_by: 'manual' | 'ci' | 'schedule';
  provider: string;
  model: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  output?: string;
  assertion_results?: { type: string; pass: boolean; score?: number; message?: string }[];
  overall_pass?: boolean;
  overall_score?: number;
  latency_ms?: number;
  is_regression?: boolean;
  review_verdict?: 'confirmed_pass' | 'confirmed_fail' | 'override_pass' | 'override_fail';
  review_notes?: string;
  requires_review?: boolean;
  is_golden?: boolean;
  golden_label?: string;
  golden_captured_at?: string;
  golden_captured_by?: string;
  error_message: string;
  created_at: string;
}

export interface JudgeCalibrationProviderSummary {
  provider: string;
  model: string;
  reviewed_runs: number;
  judge_assertions: number;
  judge_pass_rate: number;
  human_pass_rate: number;
  agreement_rate: number;
  disagreement_count: number;
}

export interface JudgeCalibrationReport {
  window_days: number;
  reviewed_runs: number;
  judge_assertions: number;
  judge_pass_rate: number;
  human_pass_rate: number;
  agreement_rate: number;
  disagreement_count: number;
  by_provider_model: JudgeCalibrationProviderSummary[];
}

export const evalApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    listSuites: builder.query<{ suites: EvalSuite[] }, void>({
      query: () => '/admin/evals/suites',
      providesTags: ['EvalSuites'],
    }),

    getSuite: builder.query<{ suite: EvalSuite; cases: EvalCase[] }, string>({
      query: (id) => `/admin/evals/suites/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'EvalSuite', id }],
    }),

    createSuite: builder.mutation<
      { suite: EvalSuite },
      Partial<
        Pick<
          EvalSuite,
          | 'name'
          | 'description'
          | 'prompt_id'
          | 'tags'
          | 'default_providers'
          | 'default_profile_slug'
          | 'ci_enabled'
        >
      >
    >({
      query: (body) => ({
        url: '/admin/evals/suites',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['EvalSuites'],
    }),

    updateSuite: builder.mutation<
      { suite: EvalSuite },
      { id: string } & Partial<
        Pick<
          EvalSuite,
          | 'name'
          | 'description'
          | 'prompt_id'
          | 'tags'
          | 'default_providers'
          | 'default_profile_slug'
          | 'ci_enabled'
        >
      >
    >({
      query: ({ id, ...body }) => ({
        url: `/admin/evals/suites/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => ['EvalSuites', { type: 'EvalSuite', id }],
    }),

    deleteSuite: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `/admin/evals/suites/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { id }) => ['EvalSuites', { type: 'EvalSuite', id }],
    }),

    getCase: builder.query<{ case: EvalCase }, string>({
      query: (id) => `/admin/evals/cases/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'EvalCase', id }],
    }),

    createCase: builder.mutation<
      { case: EvalCase },
      { suite_id: string } & Partial<
        Pick<
          EvalCase,
          | 'name'
          | 'description'
          | 'prompt_ref'
          | 'input_config'
          | 'assertions'
          | 'target_config'
          | 'scoring_config'
          | 'fixture_set_id'
          | 'source_type'
          | 'source_trace_id'
          | 'source_reviewed'
          | 'enabled'
        >
      >
    >({
      query: ({ suite_id, ...body }) => ({
        url: `/admin/evals/suites/${suite_id}/cases`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { suite_id }) => [{ type: 'EvalSuite', id: suite_id }],
    }),

    updateCase: builder.mutation<
      { case: EvalCase },
      { id: string } & Partial<
        Pick<
          EvalCase,
          | 'name'
          | 'description'
          | 'prompt_ref'
          | 'input_config'
          | 'assertions'
          | 'target_config'
          | 'scoring_config'
          | 'fixture_set_id'
          | 'enabled'
          | 'stale'
        >
      >
    >({
      query: ({ id, ...body }) => ({
        url: `/admin/evals/cases/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'EvalCase', id }],
    }),

    deleteCase: builder.mutation<void, { id: string; suite_id: string }>({
      query: ({ id }) => ({
        url: `/admin/evals/cases/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { id, suite_id }) => [
        { type: 'EvalCase', id },
        { type: 'EvalSuite', id: suite_id },
      ],
    }),

    runCase: builder.mutation<
      { run_ids: string[] },
      {
        case_id: string;
        triggered_by?: 'manual' | 'ci' | 'schedule';
        run_mode?: 'pinned' | 'latest';
        prompt_text_override?: { slug: string; text: string };
      }
    >({
      query: ({ case_id, ...body }) => ({
        url: `/admin/evals/cases/${case_id}/runs`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['EvalRuns'],
    }),

    listRuns: builder.query<
      { runs: EvalRun[]; total_count: number },
      {
        suite_id?: string;
        case_id?: string;
        requires_review?: boolean;
        is_regression?: boolean;
        limit?: number;
      }
    >({
      query: (params) => ({
        url: '/admin/evals/runs',
        params,
      }),
      providesTags: ['EvalRuns'],
    }),

    listFixtureSets: builder.query<{ fixture_sets: EvalFixtureSet[] }, void>({
      query: () => '/admin/evals/fixtures',
      providesTags: ['EvalFixtures'],
    }),

    getFixtureSet: builder.query<EvalFixtureDetailResponse, string>({
      query: (id) => `/admin/evals/fixtures/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'EvalFixtures', id }],
    }),

    previewFixtureSet: builder.mutation<
      { compiled_runtime: EvalCompiledRuntime },
      { name?: string; description?: string; authoring_config: FixtureAuthoringConfig }
    >({
      query: (body) => ({
        url: '/admin/evals/fixtures/preview',
        method: 'POST',
        body,
      }),
    }),

    createFixtureSet: builder.mutation<
      EvalFixtureDetailResponse,
      { name: string; description?: string; authoring_config: FixtureAuthoringConfig }
    >({
      query: (body) => ({
        url: '/admin/evals/fixtures',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['EvalFixtures'],
    }),

    createFixtureSetVersion: builder.mutation<
      EvalFixtureDetailResponse,
      { fixture_id: string; description?: string; authoring_config: FixtureAuthoringConfig }
    >({
      query: ({ fixture_id, ...body }) => ({
        url: `/admin/evals/fixtures/${fixture_id}/versions`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { fixture_id }) => [
        'EvalFixtures',
        { type: 'EvalFixtures', id: fixture_id },
      ],
    }),

    archiveFixtureSet: builder.mutation<void, { fixture_id: string }>({
      query: ({ fixture_id }) => ({
        url: `/admin/evals/fixtures/${fixture_id}/archive`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, { fixture_id }) => [
        'EvalFixtures',
        { type: 'EvalFixtures', id: fixture_id },
      ],
    }),

    getRun: builder.query<EvalRun, string>({
      query: (id) => `/admin/evals/runs/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'EvalRun', id }],
    }),

    reviewRun: builder.mutation<
      { run: EvalRun },
      {
        run_id: string;
        review_verdict: 'confirmed_pass' | 'confirmed_fail' | 'override_pass' | 'override_fail';
        review_notes?: string;
        requires_review?: boolean;
      }
    >({
      query: ({ run_id, ...body }) => ({
        url: `/admin/evals/runs/${run_id}/review`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { run_id }) => ['EvalRuns', { type: 'EvalRun', id: run_id }],
    }),

    captureGoldenRun: builder.mutation<
      { run: EvalRun },
      { run_id: string; golden_label?: string; golden_captured_by?: string }
    >({
      query: ({ run_id, ...body }) => ({
        url: `/admin/evals/runs/${run_id}/golden`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { run_id }) => ['EvalRuns', { type: 'EvalRun', id: run_id }],
    }),

    getJudgeCalibrationReport: builder.query<
      { report: JudgeCalibrationReport },
      { days?: number } | undefined
    >({
      query: (params) => ({
        url: '/admin/evals/calibration/judge',
        params: params ?? undefined,
      }),
    }),
  }),
});

export const {
  useListSuitesQuery,
  useGetSuiteQuery,
  useCreateSuiteMutation,
  useUpdateSuiteMutation,
  useDeleteSuiteMutation,
  useGetCaseQuery,
  useCreateCaseMutation,
  useUpdateCaseMutation,
  useDeleteCaseMutation,
  useRunCaseMutation,
  useListRunsQuery,
  useListFixtureSetsQuery,
  useGetFixtureSetQuery,
  usePreviewFixtureSetMutation,
  useCreateFixtureSetMutation,
  useCreateFixtureSetVersionMutation,
  useArchiveFixtureSetMutation,
  useGetRunQuery,
  useLazyGetRunQuery,
  useReviewRunMutation,
  useCaptureGoldenRunMutation,
  useGetJudgeCalibrationReportQuery,
} = evalApi;
